export type PageTab = {
  id: string;
  title: string;
  spaceId: string;
};

const MAX_TABS = 20;
export const VISIBLE_TABS = 15;
export const TAB_TITLE_MAX = 25;

/** Visible tab label: at most 25 grapheme-ish chars. */
export function tabLabel(title: string | null | undefined): string {
  const raw = String(title || "Untitled").replace(/\s+/g, " ").trim() || "Untitled";
  const chars = Array.from(raw);
  if (chars.length <= TAB_TITLE_MAX) return raw;
  return `${chars.slice(0, TAB_TITLE_MAX).join("")}…`;
}

export function splitVisibleTabs(tabs: PageTab[], activeId: string): { shown: PageTab[]; rest: PageTab[] } {
  if (tabs.length <= VISIBLE_TABS) return { shown: tabs, rest: [] };
  const found = tabs.findIndex((row) => row.id === activeId);
  const activeIdx = found < 0 ? 0 : found;
  let start = activeIdx - VISIBLE_TABS + 1;
  if (start < 0) start = 0;
  if (start + VISIBLE_TABS > tabs.length) start = tabs.length - VISIBLE_TABS;
  const shown = tabs.slice(start, start + VISIBLE_TABS);
  const ids = new Set(shown.map((row) => row.id));
  return { shown, rest: tabs.filter((row) => !ids.has(row.id)) };
}

function key(userId: string): string {
  return `hiera.tabs.${userId}`;
}

function valid(row: PageTab): boolean {
  return typeof row?.id === "string" && row.id.length > 0 && row.id.length <= 64;
}

export function loadPageTabs(userId: string): { tabs: PageTab[]; activeId: string } {
  try {
    const raw = localStorage.getItem(key(userId));
    if (!raw) return { tabs: [], activeId: "" };
    const parsed = JSON.parse(raw) as { tabs?: PageTab[]; activeId?: string };
    const tabs = (Array.isArray(parsed.tabs) ? parsed.tabs : [])
      .filter(valid)
      .slice(-MAX_TABS)
      .map((row) => ({
        id: row.id,
        title: String(row.title || "Untitled").slice(0, 200),
        spaceId: String(row.spaceId || ""),
      }));
    const activeId = tabs.some((row) => row.id === parsed.activeId) ? String(parsed.activeId) : tabs.at(-1)?.id || "";
    return { tabs, activeId };
  } catch {
    return { tabs: [], activeId: "" };
  }
}

export function savePageTabs(userId: string, tabs: PageTab[], activeId: string): void {
  localStorage.setItem(key(userId), JSON.stringify({ tabs, activeId }));
}

export function upsertTab(tabs: PageTab[], tab: PageTab): PageTab[] {
  const id = tab.id.trim();
  if (!id) return tabs;
  const nextTab = {
    id,
    title: tab.title.trim().slice(0, 200) || "Untitled",
    spaceId: tab.spaceId,
  };
  const idx = tabs.findIndex((row) => row.id === id);
  if (idx >= 0) {
    const cur = tabs[idx];
    if (cur.title === nextTab.title && cur.spaceId === nextTab.spaceId) return tabs;
    const next = tabs.slice();
    next[idx] = nextTab;
    return next;
  }
  const next = [...tabs, nextTab];
  return next.length > MAX_TABS ? next.slice(next.length - MAX_TABS) : next;
}
